import { useEffect, useState, type FormEvent } from "react";
import { useParams, Link } from "react-router-dom";
import type { CoachPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { ShieldCheck, ChevronLeft } from "lucide-react";

// 148 -- Coach profile. Identity is read-only (from the linked user); the
// BokSmart accreditation number is editable by school admins and by the coach
// themselves (server-enforced). The canonical home for coach-level attributes
// (a coach can be on many teams, so this lives here, not on a team row).

export default function CoachDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [coach, setCoach] = useState<CoachPublic | null>(null);
  const [boksmart, setBoksmart] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!id) return;
    api
      .getCoach(id)
      .then((res) => {
        setCoach(res.coach);
        setBoksmart(res.coach.boksmart_number ?? "");
      })
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load coach"))
      .finally(() => setLoading(false));
  }, [id]);

  const canEdit = coach?._actions.includes("edit") ?? false;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api.updateCoach(id, { boksmart_number: boksmart.trim() || null });
      setCoach(res.coach);
      setSaved(true);
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="text-muted-foreground" data-page="coach-detail" data-state="loading">Loading…</p>;
  if (error && !coach) return <p className="text-sm text-destructive" data-page="coach-detail" data-state="error" data-error>{error}</p>;
  if (!coach) return <p className="text-muted-foreground" data-page="coach-detail" data-state="empty">Coach not found</p>;

  return (
    <div className="flex flex-col gap-6" data-page="coach-detail" data-state={error ? "error" : "ready"}>
      <div>
        <Link to="/coaches" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline">
          <ChevronLeft className="h-4 w-4" /> Coaches
        </Link>
        <h1 className="mt-1 text-2xl font-semibold">{coach.user_name ?? coach.user_email}</h1>
        <p className="text-muted-foreground">{coach.school_name} · {coach.team_count} team(s)</p>
      </div>

      <Card className="max-w-lg" data-region="coach-identity">
        <CardHeader>
          <CardTitle className="text-base">Coach</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Name</span><span>{coach.user_name ?? "—"}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span>{coach.user_email}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">School</span><span>{coach.school_name}</span></div>
        </CardContent>
      </Card>

      <Card className="max-w-lg" data-region="coach-boksmart">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />
            BokSmart accreditation
          </CardTitle>
        </CardHeader>
        <CardContent>
          {canEdit ? (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-form="coach-boksmart">
              {error && <p className="text-sm text-destructive" data-error>{error}</p>}
              <div className="flex flex-col gap-2">
                <Label htmlFor="boksmart">BokSmart number</Label>
                <Input
                  id="boksmart"
                  data-field="boksmart-number"
                  value={boksmart}
                  onChange={(e) => { setBoksmart(e.target.value); setSaved(false); }}
                  placeholder="e.g. BS-12345"
                  maxLength={50}
                />
                <p className="text-xs text-muted-foreground">
                  Required on a BokSmart team sheet. Leave blank to clear.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button type="submit" data-action="save-boksmart" disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </Button>
                {saved && <span className="text-sm text-muted-foreground">Saved</span>}
              </div>
            </form>
          ) : (
            <p className="text-sm" data-field="boksmart-number">
              {coach.boksmart_number || <span className="text-muted-foreground">Not set</span>}
            </p>
          )}
        </CardContent>
      </Card>

      {canEdit && (
        <Link to="/coaches" className={buttonVariants({ variant: "outline" }) + " max-w-lg"}>
          Done
        </Link>
      )}
    </div>
  );
}
